'use client';

import { isSubject, Subject } from '@schuelerhilfe/shared/src/enums/subject';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import type { BookAppointmentModalResolved } from './types';
import { Button } from '@/components/Button';
import { ContactForm } from '@/components/form/ContactForm';
import { Modal } from '@/components/Modal';
import { submitForm } from '@/lib/api';
import { generateDefaultSlots, loadAppointmentSlots } from '@/lib/appointments';
import { toFieldTextOverrides } from '@/lib/form/fieldTexts';
import { sourceUrlWithCampaign } from '@/lib/form/sourceUrl';
import type { ContactFormSubmitData } from '@/lib/form/types';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { resolvePhonePair } from '@/lib/render/phonePair';
import { useRenderContext } from '@/lib/render/RenderContextProvider';
import { RichText } from '@/lib/render/RichText';
import {
  Step,
  SlotsStatus,
  bookingReducer,
  initialBookingState,
} from '@/src/widgets/book-appointment/bookingReducer';
import { DayPicker } from '@/src/widgets/book-appointment/DayPicker';
import { TimePicker } from '@/src/widgets/book-appointment/TimePicker';

export function BookAppointmentModalWidget({
  resolved,
}: {
  resolved: BookAppointmentModalResolved;
}) {
  const { entities, substitutePlaceholders: sub } = useRenderContext();
  // CMS text can carry `{location.*}` tokens — the Matelso phone label above is
  // the common one. The i18n fallbacks never do, so only the editor's value is
  // substituted; an empty CMS string still falls through to the fallback.
  const subText = (value: string | undefined): string | undefined =>
    value ? sub(value) : undefined;
  const loc = entities.location;
  // SH-344 item 6.1: the modal is always about the page's own Standort — there
  // is no PLZ search in it — so a partner who switched the form off takes the
  // whole modal with it, exactly as on the Standort page. Absent means no
  // preference, which shows it.
  const appointmentsOff = loc?.renderAppointmentForm === false;
  const locationSlug = loc?.slug;
  const locationName = loc?.cityName ?? loc?.name ?? '';
  // 70.07: the widget's own pair, else the page shell's resolved pair (the
  // Standort's number, or the Domain Setting's when it has none). Reading
  // `loc.phone` alone hid the call CTA on every Standort without its own
  // number, and ignored a pair configured on the widget.
  const { phone: phoneNumber, matelsoPhoneLabel } = resolvePhonePair(
    {
      phone: subText(resolved.phone),
      matelsoPhoneLabel: subText(resolved.matelsoPhoneLabel),
    },
    {
      phone: loc?.phone,
      matelsoPhoneLabel: loc?.matelsoPhoneLabel as string | undefined,
    },
    entities.domain,
  );
  const phoneLabel = matelsoPhoneLabel ?? phoneNumber;
  const tForm = useTranslations('form');
  const tBooking = useTranslations('booking');
  const tCommon = useTranslations('common');

  const [modalOpen, setModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const formDataRef = useRef<ContactFormSubmitData | null>(null);

  // The SH API Gateway's /api/form/desiredAppointment rejects an empty
  // salutation with a 400, and "Keine Angabe" maps to exactly that, so an
  // appointment offers Herr/Frau only (the contact and voucher forms, whose
  // gateway endpoints accept it, keep the third choice).
  const salutationOptions = [
    { value: 'mr', label: tForm('mr') },
    { value: 'mrs', label: tForm('mrs') },
  ];

  // Values come from the shared enum so the token the backend receives cannot
  // drift from the one its DTO accepts (SH-410); the labels stay translated.
  const subjectOptions = [
    { value: Subject.Mathe, label: tForm('subjectMathe') },
    { value: Subject.Deutsch, label: tForm('subjectDeutsch') },
    { value: Subject.Englisch, label: tForm('subjectEnglisch') },
    { value: Subject.Andere, label: tForm('subjectAndere') },
  ];

  // Booking state for the modal
  const [state, dispatch] = useReducer(
    bookingReducer,
    locationSlug,
    initialBookingState,
  );
  const { slots, slotsStatus, step, selectedDay, selectedTime, selectedMode } =
    state;

  const activeDays = useMemo(
    () =>
      slotsStatus === SlotsStatus.Fallback && locationSlug
        ? generateDefaultSlots()
        : slots,
    [slotsStatus, locationSlug, slots],
  );

  useEffect(() => {
    if (!locationSlug) {
      return;
    }
    loadAppointmentSlots(locationSlug).then((days) => {
      dispatch(
        days
          ? { type: 'SLOTS_LOADED', slots: days }
          : { type: 'SLOTS_FALLBACK' },
      );
    });
  }, [locationSlug]);

  const selectedDayData = activeDays.find((d) => d.dateIso === selectedDay);

  const handleFormValid = (data: ContactFormSubmitData): Promise<void> => {
    formDataRef.current = data;
    dispatch({ type: 'STEP_SET', step: Step.Day });
    setModalOpen(true);
    return Promise.resolve();
  };

  const handleFinalSubmit = async () => {
    const formData = formDataRef.current;
    if (!formData || !selectedDay || !selectedTime) {
      return;
    }

    try {
      await submitForm({
        type: 'appointment',
        salutation: formData.salutation,
        firstname: formData.firstname,
        lastname: formData.lastname,
        email: formData.email,
        phone: formData.phone,
        ...(formData.subject && isSubject(formData.subject)
          ? { subject: formData.subject }
          : {}),
        desiredDate: `${selectedDay} ${selectedTime}`.trim(),
        locationSlug: locationSlug ?? '',
        sourceUrl: sourceUrlWithCampaign(),
        customerNumber: formData.customerNumber,
      });
      if (resolved.tracking?.length) {
        pushCmsEventTracking(resolved.tracking);
      }
    } catch {
      // always show success — customer service handles failed bookings
    }
    if (resolved.submitTracking?.length) {
      pushCmsEventTracking(resolved.submitTracking);
    }
    setModalOpen(false);
    setSubmitted(true);
  };

  // After the hooks, so the hook order stays stable across renders.
  if (appointmentsOff) {
    return null;
  }

  if (submitted) {
    return (
      <div>
        <div className="mb-6 space-y-2">
          {(resolved.headline || locationName) && (
            <p className="text-primary text-sm font-bold uppercase">
              {resolved.headline
                ? sub(resolved.headline)
                : tBooking('headlineFallback', { locationName })}
            </p>
          )}
          <h2 className="text-foreground text-2xl font-medium">
            {resolved.subheadline
              ? sub(resolved.subheadline)
              : tBooking('subheadlineFallback')}
          </h2>
          {(resolved.description || !resolved.subheadline) && (
            <p className="text-foreground text-base">
              {resolved.description
                ? sub(resolved.description)
                : tBooking('descriptionFallback')}
            </p>
          )}
        </div>
        <div className="py-8 text-center">
          {resolved.confirmationBody && resolved.confirmationBody.length > 0 ? (
            <RichText nodes={resolved.confirmationBody} />
          ) : (
            <p>{tForm('submissionSuccess')}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 space-y-2">
        {(resolved.headline || locationName) && (
          <p className="text-primary text-sm font-bold uppercase">
            {resolved.headline
              ? sub(resolved.headline)
              : tBooking('headlineFallback', { locationName })}
          </p>
        )}
        <h2 className="text-foreground text-2xl font-medium">
          {resolved.subheadline
            ? sub(resolved.subheadline)
            : tBooking('subheadlineFallback')}
        </h2>
        {(resolved.description || !resolved.subheadline) && (
          <p className="text-foreground text-base">
            {resolved.description
              ? sub(resolved.description)
              : tBooking('descriptionFallback')}
          </p>
        )}
      </div>

      {/* Inline form */}
      <ContactForm
        onSubmit={handleFormValid}
        compact
        salutationOptions={salutationOptions}
        subjectOptions={subjectOptions.length > 0 ? subjectOptions : undefined}
        fieldTexts={toFieldTextOverrides(resolved.formTexts)}
        className="[&_input]:rounded-full [&_input]:border-0 [&_input]:bg-white [&_input]:px-4 [&_input]:text-xs [&_select]:rounded-full [&_select]:border-0 [&_select]:bg-white [&_select]:px-4 [&_select]:text-xs"
      >
        {({ canSubmit }) => (
          <div className="mt-6 space-y-3">
            <Button
              size="md"
              type="submit"
              disabled={!canSubmit}
              className="w-full"
            >
              {subText(resolved.contactFormCtaLabel) || tForm('submitButton')}
            </Button>
            {phoneNumber && (
              <Button
                href={`tel:${phoneNumber.replace(/[^+\d]/g, '')}`}
                variant="secondary"
                size="md"
                className="w-full"
              >
                {phoneLabel}
              </Button>
            )}
            {resolved.usps && resolved.usps.length > 0 && (
              <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 pt-3">
                {resolved.usps.map((usp) => (
                  <div key={usp.text} className="flex items-center gap-1">
                    <span className="text-primary-dark text-base font-bold">
                      ✓
                    </span>
                    <span className="text-primary-dark text-[11px] font-medium">
                      {usp.text}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </ContactForm>

      {/* Modal with day/time steps */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        ariaLabel={tBooking('modalAriaLabel')}
        variant="brand"
      >
        {slotsStatus === SlotsStatus.Loading && (
          <div className="flex justify-center py-16">
            <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
          </div>
        )}

        {activeDays.length > 0 && step === Step.Day && (
          <DayPicker
            availableDays={activeDays}
            selectedDay={selectedDay}
            onSelect={(dateIso) => dispatch({ type: 'DAY_SELECTED', dateIso })}
            onNext={() => {
              if (resolved.dayPickerTracking?.length) {
                pushCmsEventTracking(resolved.dayPickerTracking);
              }
              dispatch({ type: 'STEP_SET', step: Step.Time });
            }}
            ctaLabel={
              subText(resolved.dayPickerCtaLabel) || tBooking('dayPickerCta')
            }
            heading={subText(resolved.pickerTexts?.dayPickerHeading)}
            stackButtons
          />
        )}

        {step === Step.Time && selectedDayData && (
          <TimePicker
            day={selectedDayData}
            selectedTime={selectedTime}
            selectedMode={selectedMode}
            onSelect={({ time, mode }) =>
              dispatch({ type: 'TIME_SELECTED', time, mode })
            }
            onBack={() => dispatch({ type: 'STEP_SET', step: Step.Day })}
            onNext={() => {
              if (resolved.timePickerTracking?.length) {
                pushCmsEventTracking(resolved.timePickerTracking);
              }
              handleFinalSubmit();
            }}
            ctaLabel={
              subText(resolved.timePickerCtaLabel) || tBooking('timePickerCta')
            }
            backLabel={
              subText(resolved.timePickerBackLabel) ||
              tBooking('timePickerBack')
            }
            heading={subText(resolved.pickerTexts?.timePickerHeading)}
            locationGroupLabel={subText(
              resolved.pickerTexts?.timeSlotLocationLabel,
            )}
            phoneGroupLabel={subText(resolved.pickerTexts?.timeSlotPhoneLabel)}
            stackButtons
          />
        )}

        {activeDays.length === 0 && slotsStatus !== SlotsStatus.Loading && (
          <div className="py-8 text-center">
            <p className="text-foreground">
              {subText(resolved.pickerTexts?.noSlotsText) ||
                tBooking('noSlotsAvailable')}
            </p>
            <Button
              size="md"
              variant="secondary"
              onClick={() => setModalOpen(false)}
              className="mt-4"
            >
              {tCommon('close')}
            </Button>
          </div>
        )}

        {resolved.privacyText && resolved.privacyText.length > 0 ? (
          <div className="text-muted [&_a]:text-primary mt-4 text-center text-xs [&_a]:underline">
            <RichText nodes={resolved.privacyText} />
          </div>
        ) : (
          entities.privacyPolicyUrl && (
            <p className="text-muted mt-4 text-center text-xs">
              {tBooking.rich('privacyNotice', {
                websiteLink: (chunks) => (
                  <a
                    href={entities.privacyPolicyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline"
                  >
                    {chunks}
                  </a>
                ),
                locationLink: (chunks) => (
                  <a
                    href={entities.privacyPolicyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline"
                  >
                    {chunks}
                  </a>
                ),
              })}
            </p>
          )
        )}
      </Modal>
    </div>
  );
}
